'use client'

import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X, Calendar, MapPin, Award, Phone, CheckCircle, Users, MessageCircle } from 'lucide-react'
import { translateTimeSlot } from '@/utils/timeSlot'
import { parseAuthToken } from '@/utils/base64'

interface Participant {
  id: string
  name: string
  level?: string
  profile_image?: string
}

interface MatchInfo {
  matchId: string
  partnerName: string
  partnerLevel?: string
  date: string
  time: string
  location: string
  region: string
  partnerPhone?: string
  partnerUserId: string
  participantIds?: string[] // 모든 참여자 ID 배열
  participants?: Participant[] // 모든 참여자 정보
}

interface AdditionalDiver {
  userId: string
  name: string
  level?: string
  phone?: string
}

interface MatchingSuccessModalProps {
  matchInfo: MatchInfo | null
  additionalDivers?: AdditionalDiver[]
  isOpen: boolean
  onClose: () => void
  onConfirm?: () => void
  onInviteGroup?: (diverIds: string[]) => void
  theme?: 'light' | 'dark'
}

export default function MatchingSuccessModal({
  matchInfo,
  additionalDivers = [],
  isOpen,
  onClose,
  onConfirm,
  onInviteGroup,
  theme = 'dark'
}: MatchingSuccessModalProps) {
  const [isConfirming, setIsConfirming] = useState(false)
  const [selectedDivers, setSelectedDivers] = useState<string[]>([])
  const [isCreatingChat, setIsCreatingChat] = useState(false)
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null)
  const isDark = theme === 'dark'
  const isMountedRef = useRef(true)

  // 컴포넌트 마운트 상태 추적 및 portal container 설정
  useEffect(() => {
    isMountedRef.current = true
    setPortalContainer(document.body)
    return () => {
      isMountedRef.current = false
      setPortalContainer(null)
    }
  }, [])

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    })
  }

  const handleConfirm = async () => {
    if (onConfirm) {
      setIsConfirming(true)
      try {
        await onConfirm()
      } finally {
        setIsConfirming(false)
      }
    }
    onClose()
  }

  const handleDiverToggle = (diverId: string) => {
    setSelectedDivers(prev =>
      prev.includes(diverId)
        ? prev.filter(id => id !== diverId)
        : [...prev, diverId]
    )
  }

  const handleInviteGroup = async () => {
    if (onInviteGroup && selectedDivers.length > 0) {
      setIsConfirming(true)
      try {
        await onInviteGroup(selectedDivers)
      } finally {
        setIsConfirming(false)
      }
    }
    onClose()
  }

  const handleStartChat = async () => {
    if (!matchInfo) return

    setIsCreatingChat(true)
    try {
      const authToken = localStorage.getItem('auth_token')
      if (!authToken) {
        console.error('No auth token found')
        window.location.href = '/login'
        return
      }

      console.log('🔍 채팅방 찾기 시작:', {
        matchId: matchInfo.matchId,
        participantIds: matchInfo.participantIds?.length || 0,
        partnerUserId: matchInfo.partnerUserId
      })

      // 1단계: match_id로 기존 채팅방 확인 (auto-match에서 생성된 채팅방)
      const existingRoomResponse = await fetch(`/api/chat/rooms/by-match?match_id=${matchInfo.matchId}`, {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      })

      if (existingRoomResponse.ok) {
        const existingResult = await existingRoomResponse.json()
        if (existingResult.success && existingResult.data?.room_id) {
          console.log('✅ 기존 그룹 채팅방 발견:', existingResult.data.room_id)
          const roomId = existingResult.data.room_id
          // 하드 네비게이션 사용 (portal 충돌 방지)
          window.location.href = `/chat/${roomId}`
          return
        }
      }

      // 2단계: 기존 채팅방 없으면 새로 생성
      // 현재 사용자의 ID 가져오기
      const decoded = parseAuthToken(authToken)
      if (!decoded) {
        console.error('토큰 파싱 실패')
        return
      }
      const currentUserId = decoded.user_id

      // 모든 참여자 ID에서 현재 사용자 제외
      let partnerIds: string[] = []

      if (matchInfo.participantIds && matchInfo.participantIds.length > 0) {
        // participantIds가 있으면 모든 참여자 포함 (본인 제외)
        partnerIds = matchInfo.participantIds.filter(id => id !== currentUserId)
        console.log('📝 그룹 채팅방 생성:', {
          allParticipants: matchInfo.participantIds.length,
          partners: partnerIds.length
        })
      } else if (matchInfo.partnerUserId) {
        // participantIds가 없으면 1:1 채팅 (기존 방식)
        partnerIds = [matchInfo.partnerUserId]
        console.log('📝 1:1 채팅방 생성')
      }

      if (partnerIds.length === 0) {
        console.error('채팅 상대방이 없습니다')
        window.location.href = '/chat'
        return
      }

      // 채팅방 생성 또는 찾기
      const response = await fetch('/api/chat/rooms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
        body: JSON.stringify({
          partner_ids: partnerIds,
          match_id: matchInfo.matchId
        })
      })

      const result = await response.json()

      if (result.success && result.data?.room_id) {
        console.log('✅ 채팅방 생성 성공:', result.data.room_id)
        const roomId = result.data.room_id
        // 하드 네비게이션 사용 (portal 충돌 방지)
        window.location.href = `/chat/${roomId}`
      } else {
        console.error('채팅방 생성 실패:', result.error)
        // 실패해도 채팅 페이지로 이동
        window.location.href = '/chat'
      }
    } catch (error) {
      console.error('채팅방 생성 중 오류:', error)
      window.location.href = '/chat'
    } finally {
      if (isMountedRef.current) {
        setIsCreatingChat(false)
      }
    }
  }

  // 모달이 닫혀있거나 portal container가 없으면 렌더링하지 않음
  if (!isOpen || !matchInfo || !portalContainer) return null

  // createPortal 사용 (z-index 문제 해결을 위해 document.body에 렌더링)
  const modalContent = (
    <div className="fixed inset-0 z-[99999] overflow-y-auto">
      {/* 배경 오버레이 */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* 모달 컨텐츠 - 30% 확대됨 */}
      <div className="flex min-h-full items-center justify-center p-2 xxs:p-3 xs:p-5">
        <div className={`relative rounded-xl xxs:rounded-2xl max-w-md w-full border animate-pulse-once ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          {/* 헤더 */}
          <div className="bg-gradient-to-r from-cyan-500 to-blue-500 rounded-t-xl xxs:rounded-t-2xl p-3.5 xxs:p-4 text-center relative">
            <button
              onClick={onClose}
              className="absolute top-2 right-2 xxs:top-2.5 xxs:right-2.5 p-1.5 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors"
            >
              <X className="w-5 h-5 xxs:w-5 xxs:h-5" />
            </button>

            <div className="flex items-center justify-center mb-2 xxs:mb-3">
              <CheckCircle className="w-9 h-9 xxs:w-10 xxs:h-10 text-white mr-2" />
              <Users className="w-8 h-8 xxs:w-9 xxs:h-9 text-white" />
            </div>

            <h2 className="text-base xxs:text-lg font-bold text-white mb-1">매칭 성공!</h2>
            <p className="text-white/90 text-[13px] xxs:text-sm">
              다이빙 버디가 매칭되었습니다
            </p>
          </div>

          {/* 컨텐츠 */}
          <div className="p-3.5 xxs:p-4 space-y-3 xxs:space-y-3.5 max-h-[60vh] overflow-y-auto">
            {/* 버디 정보 - 모든 참여자 표시 */}
            <div className={`rounded-lg xxs:rounded-xl p-3 xxs:p-3.5 ${isDark ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
              <h3 className={`text-[13px] xxs:text-sm font-semibold mb-2 flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <Users className="w-4 h-4 xxs:w-4.5 xxs:h-4.5 mr-1.5 text-blue-400" />
                버디 정보
                {matchInfo.participants && matchInfo.participants.length > 0 && (
                  <span className={`ml-1.5 text-[12px] font-normal ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    ({matchInfo.participants.length}명)
                  </span>
                )}
              </h3>

              {/* 참여자가 여러 명인 경우 */}
              {matchInfo.participants && matchInfo.participants.length > 0 ? (
                <div className="space-y-2">
                  {matchInfo.participants.map((participant, index) => (
                    <div key={participant.id} className={`flex items-center gap-3 p-2 rounded-lg ${isDark ? 'bg-gray-600/50' : 'bg-white'}`}>
                      {/* 프로필 이미지 */}
                      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                        {participant.profile_image ? (
                          <img
                            src={participant.profile_image}
                            alt={participant.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
                            <span className="text-white font-bold text-sm">
                              {participant.name.charAt(0)}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* 참여자 정보 */}
                      <div className="flex-1 min-w-0">
                        <div className={`font-medium text-sm truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {participant.name}
                        </div>
                        {participant.level && (
                          <div className={`text-xs flex items-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            <Award className="w-3 h-3 mr-1 text-yellow-400" />
                            {participant.level}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* 기존 단일 버디 정보 (fallback) */
                <div className="space-y-1.5 xxs:space-y-2 text-[13px] xxs:text-sm">
                  <div className="flex items-center justify-between">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>이름</span>
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{matchInfo.partnerName}</span>
                  </div>

                  {matchInfo.partnerLevel && (
                    <div className="flex items-center justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>자격증</span>
                      <div className="flex items-center">
                        <Award className="w-3.5 h-3.5 xxs:w-4 xxs:h-4 mr-1 text-yellow-400" />
                        <span className={isDark ? 'text-white' : 'text-gray-900'}>{matchInfo.partnerLevel}</span>
                      </div>
                    </div>
                  )}

                  {matchInfo.partnerPhone && (
                    <div className="flex items-center justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>연락처</span>
                      <div className="flex items-center">
                        <Phone className="w-3.5 h-3.5 xxs:w-4 xxs:h-4 mr-1 text-green-400" />
                        <span className={isDark ? 'text-white' : 'text-gray-900'}>{matchInfo.partnerPhone}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* 다이빙 일정 */}
            <div className={`rounded-lg xxs:rounded-xl p-3 xxs:p-3.5 ${isDark ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
              <h3 className={`text-[13px] xxs:text-sm font-semibold mb-2 flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <Calendar className="w-4 h-4 xxs:w-4.5 xxs:h-4.5 mr-1.5 text-blue-400" />
                다이빙 일정
              </h3>

              <div className="space-y-1.5 xxs:space-y-2 text-[13px] xxs:text-sm">
                <div className="flex items-center justify-between">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>날짜</span>
                  <span className={isDark ? 'text-white' : 'text-gray-900'}>{formatDate(matchInfo.date)}</span>
                </div>

                <div className="flex items-center justify-between">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>시간</span>
                  <span className={isDark ? 'text-white' : 'text-gray-900'}>
                    {matchInfo.time ? translateTimeSlot(matchInfo.time) : '시간 미정'}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>풀장</span>
                  <div className="flex items-center">
                    <MapPin className="w-3.5 h-3.5 xxs:w-4 xxs:h-4 mr-1 text-red-400" />
                    <span className={isDark ? 'text-white' : 'text-gray-900'}>{matchInfo.location || matchInfo.region}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 안내 메시지 */}
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg xxs:rounded-xl p-2 xxs:p-3">
              <p className="text-blue-400 text-[12px] xxs:text-[13px] text-center leading-relaxed">
                📱 버디와 연락하여 다이빙 계획을 세워보세요!<br/>
                🤿 안전한 다이빙 되시길 바랍니다.
              </p>
            </div>

            {/* 추가 다이버 목록 */}
            {additionalDivers && additionalDivers.length > 0 && (
              <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-blue-500/20 rounded-lg xxs:rounded-xl p-3 xxs:p-3.5">
                <h3 className={`text-[13px] xxs:text-sm font-semibold mb-1.5 xxs:mb-2 flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <Users className="w-4 h-4 xxs:w-4.5 xxs:h-4.5 mr-1.5 text-blue-400" />
                  같은 시간대 다이버
                  <span className={`text-[12px] xxs:text-[13px] font-normal ml-1.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    ({additionalDivers.length}명)
                  </span>
                </h3>

                <p className={`text-[12px] xxs:text-[13px] mb-2.5 ${isDark ? 'text-blue-400' : 'text-blue-500'}`}>
                  🌊 그룹 다이빙을 원하시면 초대해보세요!
                </p>

                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {additionalDivers.map((diver) => (
                    <div
                      key={diver.userId}
                      className={`flex items-center justify-between p-2 xxs:p-2.5 rounded-lg border transition-all cursor-pointer ${
                        selectedDivers.includes(diver.userId)
                          ? 'bg-blue-500/20 border-blue-500/40'
                          : isDark
                            ? 'bg-gray-700/50 border-gray-600 hover:bg-gray-700/70'
                            : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                      }`}
                      onClick={() => handleDiverToggle(diver.userId)}
                    >
                      <div className="flex items-center space-x-2 xxs:space-x-2.5">
                        <div className={`w-7 h-7 xxs:w-8 xxs:h-8 rounded-full flex items-center justify-center text-[13px] xxs:text-sm ${
                          selectedDivers.includes(diver.userId)
                            ? 'bg-blue-500 text-white'
                            : isDark ? 'bg-gray-600 text-gray-300' : 'bg-gray-300 text-gray-700'
                        }`}>
                          {diver.name.charAt(0)}
                        </div>
                        <div>
                          <div className={`text-[13px] xxs:text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{diver.name}</div>
                          {diver.level && (
                            <div className={`text-[12px] xxs:text-[13px] flex items-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              <Award className="w-2.5 h-2.5 mr-1" />
                              {diver.level}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className={`w-5 h-5 xxs:w-5 xxs:h-5 rounded border-2 flex items-center justify-center ${
                        selectedDivers.includes(diver.userId)
                          ? 'border-blue-500 bg-blue-500'
                          : isDark ? 'border-gray-500' : 'border-gray-400'
                      }`}>
                        {selectedDivers.includes(diver.userId) && (
                          <CheckCircle className="w-3 h-3 xxs:w-3.5 xxs:h-3.5 text-white" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {selectedDivers.length > 0 && (
                  <div className="mt-2.5 p-2 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                    <p className={`text-[12px] xxs:text-[13px] text-center ${isDark ? 'text-blue-400' : 'text-blue-500'}`}>
                      ✨ {selectedDivers.length}명 그룹 초대
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 확인 버튼 */}
          <div className="p-3.5 xxs:p-4 pt-0 space-y-2 xxs:space-y-2.5">
            {/* 채팅 시작하기 버튼 */}
            <button
              onClick={handleStartChat}
              disabled={isCreatingChat}
              className="w-full px-4 py-2.5 xxs:py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-lg xxs:rounded-xl text-sm xxs:text-base font-medium transition-all flex items-center justify-center space-x-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <MessageCircle className="w-4 h-4 xxs:w-5 xxs:h-5" />
              <span>{isCreatingChat ? '생성 중...' : '채팅 시작하기'}</span>
            </button>

            {/* 기존 버튼들 */}
            <div className="flex space-x-2 xxs:space-x-2.5">
              {selectedDivers.length > 0 ? (
                <>
                  <button
                    onClick={onClose}
                    className={`flex-1 px-4 py-2.5 xxs:py-3 text-white rounded-lg xxs:rounded-xl text-sm xxs:text-base font-medium transition-colors ${isDark ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-400 hover:bg-gray-500'}`}
                  >
                    취소
                  </button>
                  <button
                    onClick={handleInviteGroup}
                    disabled={isConfirming}
                    className="flex-1 px-4 py-2.5 xxs:py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-lg xxs:rounded-xl text-sm xxs:text-base font-medium transition-all disabled:opacity-50"
                  >
                    {isConfirming ? '초대 중...' : `${selectedDivers.length}명 초대`}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={onClose}
                    className={`flex-1 px-4 py-2.5 xxs:py-3 text-white rounded-lg xxs:rounded-xl text-sm xxs:text-base font-medium transition-colors ${isDark ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-400 hover:bg-gray-500'}`}
                  >
                    나중에
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={isConfirming}
                    className="flex-1 px-4 py-2.5 xxs:py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-lg xxs:rounded-xl text-sm xxs:text-base font-medium transition-all disabled:opacity-50"
                  >
                    {isConfirming ? '처리 중...' : '확인'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, portalContainer)
}